import { Button, ConfigProvider, DatePicker, Empty, Input, Layout, Modal, Space, Table, Tag, notification } from "antd";
import type { Dayjs } from "dayjs";
import type { TableColumnsType } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { HistoryOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSearchParams } from "react-router-dom";
import {
    getAccountPermissionAction,
    getAppReleaseNotes,
    getAppVersionHistory,
    getAppVersions,
    saveAppReleaseNotes,
    setAppReleaseApprovalStatus
} from "../../service/httpsCalls";
import { LoaderSpinner } from "../../components/common/Loader";

type NotificationType = "success" | "info" | "warning" | "error";
type EditorMode = "add" | "edit";

interface AppVersionRow {
    appVersionId?: number;
    appModuleId: number;
    moduleName: string;
    versionNumber: string;
    plannedReleaseDate?: string;
    releasedOn?: string;
    isApproved: boolean;
    canApprove: boolean;
    releaseApprovedBy?: string;
}

interface AppReleaseNoteDetails {
    appVersionId: number;
    appModuleId: number;
    moduleName: string;
    versionNumber: string;
    releaseDate?: string;
    noteTitle?: string;
    notesText?: string;
    isApproved: boolean;
    canApprove: boolean;
    releaseApprovedBy?: string;
    createdBy?: string;
    createdOn?: string;
}

interface AppVersionHistoryRow {
    appVersionId: number;
    versionNumber: string;
    releaseDate?: string;
    isApproved: boolean;
    releaseApprovedBy?: string;
    createdOn: string;
}

interface PermissionActionAccess {
    permissionId: number;
    isCreatingPermission?: boolean | null;
    isEditingPermission?: boolean | null;
    isDeletingPermission?: boolean | null;
    isSpecialFeature: boolean;
}

export default function AppVersions() {
    const [api, contextHolder] = notification.useNotification();
    const [searchParams] = useSearchParams();
    const [rows, setRows] = useState<AppVersionRow[]>([]);
    const [loading, setLoading] = useState(false);

    const [selectedRow, setSelectedRow] = useState<AppVersionRow | null>(null);
    const [editorMode, setEditorMode] = useState<EditorMode>("add");
    const [editingAppVersionId, setEditingAppVersionId] = useState<number | null>(null);

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);

    const [saveLoading, setSaveLoading] = useState(false);
    const [releaseNotesLoading, setReleaseNotesLoading] = useState(false);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [versionNumber, setVersionNumber] = useState("");
    const [notesText, setNotesText] = useState("");
    const [releaseDate, setReleaseDate] = useState<Dayjs | null>(null);

    const [releaseNoteDetails, setReleaseNoteDetails] = useState<AppReleaseNoteDetails | null>(null);
    const [historyRows, setHistoryRows] = useState<AppVersionHistoryRow[]>([]);
    const [historyModuleName, setHistoryModuleName] = useState("");
    const [historyModuleId, setHistoryModuleId] = useState(0);
    const [permissionActionAccess, setPermissionActionAccess] = useState<PermissionActionAccess | null>(null);

    const releaseNotesEditorModules = {
        toolbar: [
            [{ font: [] }],
            [{ header: [] }],
            ["bold", "italic", "underline", "strike"],
            ["blockquote", "code-block"],
            ["link"],
            [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ["clean"]
        ]
    };

    const moduleDotColorMap: Record<number, string> = {
        1: "#389e0d",
        2: "#1677ff",
        3: "#fa8c16",
        4: "#722ed1",
        5: "#13c2c2",
        6: "#d4380d"
    };

    const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
        api[type]({
            message: message,
            description: description
        });
    };

    const getReleaseDate = (record: AppVersionRow) => record.releasedOn ?? record.plannedReleaseDate;

    const isEditorContentEmpty = (value: string) =>
        !value || value.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim() === "";

    const canCreate = permissionActionAccess?.isCreatingPermission === true;
    const canEdit = permissionActionAccess?.isEditingPermission === true;
    const canUseSpecialFeature = permissionActionAccess?.isSpecialFeature === true;

    function resolvePermissionIdFromUserData() {
        try {
            const userDataRaw = window.localStorage.getItem("userData");
            if (!userDataRaw) {
                return 0;
            }

            const userData = JSON.parse(userDataRaw);
            const permissionSource = userData?.permissions ?? userData?.Permissions ?? [];
            const permissionList = Array.isArray(permissionSource) ? permissionSource : Object.values(permissionSource);

            const appVersionPermission: any = permissionList.find((item: any) => {
                const url = (item?.url ?? item?.Url ?? "").toString().toLowerCase();
                return url.includes("app/versions") || url.includes("app/version") || url.includes("appversions");
            });

            const permissionId = Number(appVersionPermission?.permissionId ?? appVersionPermission?.PermissionId ?? 0);
            return Number.isNaN(permissionId) ? 0 : permissionId;
        } catch {
            return 0;
        }
    }

    function normalizeAppVersionRow(item: any): AppVersionRow {
        return {
            appVersionId: item?.appVersionId ?? item?.AppVersionId ?? undefined,
            appModuleId: item?.appModuleId ?? item?.AppModuleId ?? 0,
            moduleName: item?.moduleName ?? item?.ModuleName ?? "",
            versionNumber: item?.versionNumber ?? item?.VersionNumber ?? "",
            plannedReleaseDate: item?.plannedReleaseDate ?? item?.PlannedReleaseDate ?? undefined,
            releasedOn: item?.releasedOn ?? item?.ReleasedOn ?? undefined,
            isApproved: item?.isApproved ?? item?.IsApproved ?? false,
            canApprove: item?.canApprove ?? item?.CanApprove ?? false,
            releaseApprovedBy: item?.releaseApprovedBy ?? item?.ReleaseApprovedBy ?? undefined
        };
    }

    function normalizeReleaseNote(item: any): AppReleaseNoteDetails {
        return {
            appVersionId: item?.appVersionId ?? item?.AppVersionId ?? 0,
            appModuleId: item?.appModuleId ?? item?.AppModuleId ?? 0,
            moduleName: item?.moduleName ?? item?.ModuleName ?? "",
            versionNumber: item?.versionNumber ?? item?.VersionNumber ?? "",
            releaseDate: item?.releaseDate ?? item?.ReleaseDate ?? undefined,
            noteTitle: item?.noteTitle ?? item?.NoteTitle ?? undefined,
            notesText: item?.notesText ?? item?.NotesText ?? "",
            isApproved: item?.isApproved ?? item?.IsApproved ?? false,
            canApprove: item?.canApprove ?? item?.CanApprove ?? false,
            releaseApprovedBy: item?.releaseApprovedBy ?? item?.ReleaseApprovedBy ?? undefined,
            createdBy: item?.createdBy ?? item?.CreatedBy ?? undefined,
            createdOn: item?.createdOn ?? item?.CreatedOn ?? undefined
        };
    }

    function normalizeHistoryRow(item: any): AppVersionHistoryRow {
        return {
            appVersionId: item?.appVersionId ?? item?.AppVersionId ?? 0,
            versionNumber: item?.versionNumber ?? item?.VersionNumber ?? "",
            releaseDate: item?.releaseDate ?? item?.ReleaseDate ?? undefined,
            isApproved: item?.isApproved ?? item?.IsApproved ?? false,
            releaseApprovedBy: item?.releaseApprovedBy ?? item?.ReleaseApprovedBy ?? undefined,
            createdOn: item?.createdOn ?? item?.CreatedOn ?? ""
        };
    }

    function resetEditorState() {
        setVersionNumber("");
        setReleaseDate(null);
        setNotesText("");
        setEditingAppVersionId(null);
        setEditorMode("add");
    }

    function loadAppVersions() {
        setLoading(true);
        getAppVersions((res: any) => {
            setLoading(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!";
                openNotificationWithIcon("error", errorMsg, "");
                return;
            }
            const normalizedRows = Array.isArray(res) ? res.map((x) => normalizeAppVersionRow(x)) : [];
            setRows(normalizedRows);
        });
    }

    function loadReleaseNoteDetails(appVersionId: number, onLoaded?: (data: AppReleaseNoteDetails) => void) {
        setReleaseNotesLoading(true);
        getAppReleaseNotes(appVersionId, (res: any) => {
            setReleaseNotesLoading(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!";
                openNotificationWithIcon("error", errorMsg, "");
                return;
            }

            const normalized = normalizeReleaseNote(res);
            setReleaseNoteDetails(normalized);
            if (onLoaded) {
                onLoaded(normalized);
            }
        });
    }

    function openAddNewModal(record: AppVersionRow) {
        setSelectedRow(record);
        setEditorMode("add");
        setEditingAppVersionId(null);
        setVersionNumber("");
        setReleaseDate(dayjs());
        setNotesText("");
        setAddModalOpen(true);
    }

    function openEditModal(record: AppVersionRow) {
        if (record.appVersionId == null) {
            openNotificationWithIcon("warning", "No version found for selected module.", "");
            return;
        }

        setSelectedRow(record);
        setEditorMode("edit");
        setEditingAppVersionId(record.appVersionId);

        const value = getReleaseDate(record);
        setVersionNumber(record.versionNumber ?? "");
        setReleaseDate(value ? dayjs(value) : null);
        setNotesText("");
        setAddModalOpen(true);

        loadReleaseNoteDetails(record.appVersionId, (data) => {
            setVersionNumber(data.versionNumber ?? record.versionNumber ?? "");
            setReleaseDate(data.releaseDate ? dayjs(data.releaseDate) : value ? dayjs(value) : null);
            setNotesText(data.notesText ?? "");
        });
    }

    function openViewModal(record: AppVersionRow) {
        if (record.appVersionId == null) {
            openNotificationWithIcon("warning", "No version found for selected module.", "");
            return;
        }

        setSelectedRow(record);
        setReleaseNoteDetails(null);
        setViewModalOpen(true);
        loadReleaseNoteDetails(record.appVersionId);
    }

    function openViewModalByVersionId(appVersionId: number, appModuleId: number, moduleName: string) {
        setSelectedRow({
            appVersionId,
            appModuleId,
            moduleName,
            versionNumber: "",
            isApproved: false,
            canApprove: false
        });
        setReleaseNoteDetails(null);
        setViewModalOpen(true);
        loadReleaseNoteDetails(appVersionId);
    }

    function openVersionHistory(record: AppVersionRow) {
        setSelectedRow(record);
        setHistoryModalOpen(true);
        setHistoryRows([]);
        setHistoryModuleName(record.moduleName);
        setHistoryModuleId(record.appModuleId);
        setHistoryLoading(true);

        getAppVersionHistory(record.appModuleId, (res: any) => {
            setHistoryLoading(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!";
                openNotificationWithIcon("error", errorMsg, "");
                return;
            }

            setHistoryRows(Array.isArray(res) ? res.map((x) => normalizeHistoryRow(x)) : []);
        });
    }

    function saveReleaseNotes() {
        if (!selectedRow) {
            openNotificationWithIcon("warning", "Module is required.", "");
            return;
        }

        if (!versionNumber || versionNumber.trim() === "") {
            openNotificationWithIcon("warning", "Please enter version number.", "");
            return;
        }

        if (!releaseDate) {
            openNotificationWithIcon("warning", "Please select release date.", "");
            return;
        }

        if (isEditorContentEmpty(notesText)) {
            openNotificationWithIcon("warning", "Please enter release notes.", "");
            return;
        }

        const appVersionIdToSave = editorMode === "edit" ? editingAppVersionId : null;
        if (editorMode === "edit" && !appVersionIdToSave) {
            openNotificationWithIcon("warning", "Version id is required for edit.", "");
            return;
        }

        setSaveLoading(true);
        saveAppReleaseNotes(
            {
                appModuleId: selectedRow.appModuleId,
                appVersionId: appVersionIdToSave,
                versionNumber: versionNumber.trim(),
                releaseDate: releaseDate.format("YYYY-MM-DDTHH:mm:ss"),
                notesText: notesText
            },
            (res: any) => {
                setSaveLoading(false);
                if (res?.hasError) {
                    const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!";
                    openNotificationWithIcon("error", errorMsg, "");
                    return;
                }

                openNotificationWithIcon("success", editorMode === "edit" ? "Release note updated successfully." : "Release note saved successfully.", "");
                setAddModalOpen(false);
                resetEditorState();
                loadAppVersions();
            }
        );
    }

    function setApprovalStatus(isApproved: boolean) {
        if (!releaseNoteDetails?.appVersionId) {
            return;
        }

        setApprovalLoading(true);
        setAppReleaseApprovalStatus(
            {
                appVersionId: releaseNoteDetails.appVersionId,
                isApproved: isApproved
            },
            (res: any) => {
                setApprovalLoading(false);
                if (res?.hasError) {
                    const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!";
                    openNotificationWithIcon("error", errorMsg, "");
                    return;
                }

                openNotificationWithIcon("success", isApproved ? "Release note approved." : "Marked as not approved.", "");
                loadReleaseNoteDetails(releaseNoteDetails.appVersionId);
                loadAppVersions();
            }
        );
    }

    const columns: TableColumnsType<AppVersionRow> = [
        {
            title: "Module",
            dataIndex: "moduleName",
            key: "moduleName",
            render: (value: string, record) => (
                <Space size={8}>
                    <span
                        style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            display: "inline-block",
                            backgroundColor: moduleDotColorMap[record.appModuleId] ?? "#999"
                        }}
                    />
                    <span>{value}</span>
                </Space>
            )
        },
        {
            title: "Version",
            dataIndex: "versionNumber",
            key: "versionNumber",
            render: (value: string, record) => {
                if (!value || !record.appVersionId) {
                    return "-";
                }

                return (
                    <Button type="link" style={{ padding: 0 }} onClick={() => openViewModal(record)}>
                        {value}
                    </Button>
                );
            }
        },
        {
            title: "Release Date",
            key: "releaseDate",
            render: (_, record) => {
                const value = getReleaseDate(record);
                return value ? dayjs(value).format("MMM DD, YYYY") : "-";
            }
        },
        ...(canCreate || canEdit
            ? [
                  {
                      title: "Action",
                      key: "action",
                      width: 240,
                      render: (_: any, record: AppVersionRow) => (
                          <Space>
                              {canCreate && (
                                  <Button type="primary" onClick={() => openAddNewModal(record)}>
                                      Add New
                                  </Button>
                              )}
                              {canEdit && (
                                  <Button disabled={record.appVersionId == null} onClick={() => openEditModal(record)}>
                                      Edit
                                  </Button>
                              )}
                          </Space>
                      )
                  }
              ]
            : []),
        {
            title: "Version History",
            key: "versionHistory",
            width: 120,
            render: (_, record) =>
                record.appVersionId != null ? (
                    <Button type="link" title="Version History" icon={<HistoryOutlined />} onClick={() => openVersionHistory(record)} />
                ) : null
        }
    ];

    const historyColumns: TableColumnsType<AppVersionHistoryRow> = [
        {
            title: "Version",
            dataIndex: "versionNumber",
            key: "versionNumber",
            render: (value: string, record) => (
                <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => {
                        setHistoryModalOpen(false);
                        if (historyModuleId > 0) {
                            openViewModalByVersionId(record.appVersionId, historyModuleId, historyModuleName);
                        }
                    }}
                >
                    {value}
                </Button>
            )
        },
        {
            title: "Release Date",
            key: "releaseDate",
            render: (_, record) => (record.releaseDate ? dayjs(record.releaseDate).format("MMM DD, YYYY") : "-")
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => (record.isApproved ? <Tag color="green">Approved</Tag> : <Tag color="orange">Not Approved</Tag>)
        },
        {
            title: "Created On",
            dataIndex: "createdOn",
            key: "createdOn",
            render: (value: string) => (value ? dayjs(value).format("MMM DD, YYYY hh:mm A") : "-")
        }
    ];

    useEffect(() => {
        document.title = "App Versions";
        loadAppVersions();
    }, []);

    useEffect(() => {
        const permissionIdValue = searchParams.get("permissionId");
        const permissionIdFromQuery = permissionIdValue ? Number(permissionIdValue) : 0;
        const permissionId = permissionIdFromQuery && !Number.isNaN(permissionIdFromQuery)
            ? permissionIdFromQuery
            : resolvePermissionIdFromUserData();

        if (!permissionId || Number.isNaN(permissionId)) {
            setPermissionActionAccess(null);
            return;
        }

        getAccountPermissionAction(permissionId, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Unable to load permission access.";
                openNotificationWithIcon("error", errorMsg, "");
                setPermissionActionAccess(null);
                return;
            }

            setPermissionActionAccess({
                permissionId: res?.permissionId ?? res?.PermissionId ?? permissionId,
                isCreatingPermission: res?.isCreatingPermission ?? res?.IsCreatingPermission ?? null,
                isEditingPermission: res?.isEditingPermission ?? res?.IsEditingPermission ?? null,
                isDeletingPermission: res?.isDeletingPermission ?? res?.IsDeletingPermission ?? null,
                isSpecialFeature: res?.isSpecialFeature ?? res?.IsSpecialFeature ?? false
            });
        });
    }, [searchParams]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
                }
            }}
        >
            {contextHolder}
            <style>{`
                .release-notes-editor .ql-container {
                    min-height: 300px;
                }
                .release-notes-editor .ql-editor {
                    min-height: 300px;
                }
            `}</style>
            <Layout
                style={{
                    padding: "20px",
                    marginBottom: "5px",
                    marginTop: "5px"
                }}
            >
                <h3 style={{ textTransform: "uppercase", marginTop: "0px" }}>App Versions</h3>
                <Table
                    style={{ background: "#fff" }}
                    dataSource={rows}
                    columns={columns}
                    pagination={false}
                    rowKey={(record) => `${record.appModuleId}`}
                    loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                />
            </Layout>

            <Modal
                title={editorMode === "edit" ? "Edit Release Notes" : "Add Release Notes"}
                open={addModalOpen}
                onCancel={() => {
                    setAddModalOpen(false);
                    resetEditorState();
                }}
                onOk={saveReleaseNotes}
                okText={editorMode === "edit" ? "Update" : "Save"}
                confirmLoading={saveLoading}
                destroyOnClose
                width={900}
            >
                <div style={{ marginBottom: "12px" }}>
                    <strong>Selected Module:</strong> {selectedRow?.moduleName ?? "-"}
                </div>
                <div style={{ marginBottom: "12px", display: "flex", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                        <label>Version Number</label>
                        <Input
                            style={{ marginTop: "6px" }}
                            value={versionNumber}
                            onChange={(e) => setVersionNumber(e.target.value)}
                            maxLength={50}
                            placeholder="Enter version number"
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label>Release Date</label>
                        <DatePicker
                            style={{ width: "100%", marginTop: "6px" }}
                            value={releaseDate}
                            onChange={(value) => setReleaseDate(value)}
                            format="MMM DD, YYYY"
                        />
                    </div>
                </div>
                <div style={{ marginBottom: "10px" }}>
                    <label>Release Notes</label>
                    <div style={{ marginTop: "6px" }}>
                        <ReactQuill
                            className="release-notes-editor"
                            theme="snow"
                            value={notesText}
                            modules={releaseNotesEditorModules}
                            onChange={(value) => setNotesText(value)}
                            placeholder="Enter release notes..."
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                title="Release Note"
                open={viewModalOpen}
                onCancel={() => {
                    setViewModalOpen(false);
                    setReleaseNoteDetails(null);
                }}
                footer={null}
                destroyOnClose
                width={900}
            >
                {releaseNotesLoading && <LoaderSpinner size={24} topMargin="10px" />}
                {!releaseNotesLoading && releaseNoteDetails && (
                    <>
                        <div style={{ marginBottom: "8px" }}>
                            <strong>Module:</strong> {releaseNoteDetails.moduleName}
                        </div>
                        <div style={{ marginBottom: "8px" }}>
                            <strong>Version:</strong> {releaseNoteDetails.versionNumber || "-"}
                        </div>
                        <div style={{ marginBottom: "12px" }}>
                            <strong>Release Date:</strong>{" "}
                            {releaseNoteDetails.releaseDate ? dayjs(releaseNoteDetails.releaseDate).format("MMM DD, YYYY") : "-"}
                        </div>
                        <div style={{ marginBottom: "12px" }}>
                            {releaseNoteDetails.isApproved ? <Tag color="green">Approved</Tag> : <Tag color="orange">Not Approved</Tag>}
                            {releaseNoteDetails.releaseApprovedBy && <Tag color="blue">By: {releaseNoteDetails.releaseApprovedBy}</Tag>}
                        </div>

                        {releaseNoteDetails.canApprove && canUseSpecialFeature && !releaseNoteDetails.isApproved && (
                            <Space style={{ marginBottom: "14px" }}>
                                <Button type="primary" loading={approvalLoading} onClick={() => setApprovalStatus(true)}>
                                    Approve
                                </Button>
                                <Button loading={approvalLoading} onClick={() => setApprovalStatus(false)}>
                                    Not Approved
                                </Button>
                            </Space>
                        )}

                        {!releaseNoteDetails.canApprove && (
                            <div style={{ marginBottom: "14px", color: "#8c8c8c" }}>
                                Approval action is available only when release date is in future.
                            </div>
                        )}

                        {releaseNoteDetails.notesText ? (
                            <div
                                style={{
                                    border: "1px solid #f0f0f0",
                                    borderRadius: "6px",
                                    padding: "12px",
                                    background: "#fff"
                                }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: releaseNoteDetails.notesText }} />
                                {(releaseNoteDetails.createdBy || releaseNoteDetails.createdOn) && (
                                    <div style={{ marginTop: "8px", color: "#8c8c8c", fontSize: "12px" }}>
                                        {releaseNoteDetails.createdBy ?? ""}
                                        {releaseNoteDetails.createdOn ? ` | ${dayjs(releaseNoteDetails.createdOn).format("MMM DD, YYYY hh:mm A")}` : ""}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Empty description="No release note added." />
                        )}
                    </>
                )}
            </Modal>

            <Modal
                title={`Version History${historyModuleName ? ` - ${historyModuleName}` : ""}`}
                open={historyModalOpen}
                onCancel={() => {
                    setHistoryModalOpen(false);
                    setHistoryRows([]);
                    setHistoryModuleName("");
                    setHistoryModuleId(0);
                }}
                footer={null}
                destroyOnClose
                width={900}
            >
                <Table
                    dataSource={historyRows}
                    columns={historyColumns}
                    pagination={false}
                    rowKey={(record) => record.appVersionId}
                    locale={{ emptyText: <Empty description="No versions found for this module." /> }}
                    loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: historyLoading }}
                />
            </Modal>
        </ConfigProvider>
    );
}
